import { useState } from 'react';
import { MessageSquarePlus, Loader2, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'Proceso', label: 'Proceso', description: 'Sobre el proceso general de la operación' },
  { value: 'Comunicación', label: 'Comunicación', description: 'Sobre la comunicación y atención' },
  { value: 'Documentación', label: 'Documentación', description: 'Sobre el manejo de documentos' },
  { value: 'Cierre', label: 'Cierre', description: 'Sobre la conclusión de la operación' }
];

export const RecommendationModal = ({ isOpen, onClose, professional, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    author_name: '',
    text: '',
    category: ''
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.text.length > 280) {
      setError('La recomendación no puede exceder 280 caracteres');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${API}/recommendations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          professional_id: professional.id
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Error al enviar la recomendación');
      }

      setIsSuccess(true);
      if (onSuccess) {
        const newRec = await response.json();
        onSuccess(newRec);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ author_name: '', text: '', category: '' });
    setIsSuccess(false);
    setError(null);
    onClose();
  };

  if (!professional) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg" data-testid="recommendation-modal">
        <DialogHeader>
          <DialogTitle className="font-manrope text-xl">
            Escribir Recomendación
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Comparte tu experiencia profesional con {professional.nombre}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="py-8 text-center" data-testid="recommendation-success">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-manrope font-semibold text-lg text-slate-900 mb-2">
              Recomendación Enviada
            </h3>
            <p className="text-slate-600 text-sm mb-6">
              Gracias por tu recomendación. Será visible una vez aprobada por el sistema.
            </p>
            <Button onClick={handleClose} className="bg-slate-900 hover:bg-slate-800">
              Cerrar
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Guidelines */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-xs text-slate-600 font-medium mb-2">Lineamientos de recomendación:</p>
              <ul className="text-xs text-slate-500 space-y-1">
                <li>• Solo se permiten recomendaciones positivas o neutras</li>
                <li>• Usa lenguaje profesional y objetivo</li>
                <li>• Máximo 280 caracteres</li>
              </ul>
            </div>

            {/* Author Name */}
            <div>
              <Label htmlFor="author" className="text-sm font-medium text-slate-700">
                Tu nombre *
              </Label>
              <Input
                id="author"
                type="text"
                required
                value={formData.author_name}
                onChange={(e) => handleChange('author_name', e.target.value)}
                className="mt-1 border-slate-200"
                placeholder="Nombre o empresa"
                data-testid="recommendation-author-input"
              />
            </div>

            {/* Category */}
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Categoría *
              </Label>
              <Select
                value={formData.category}
                onValueChange={(value) => handleChange('category', value)}
                required
              >
                <SelectTrigger className="mt-1 border-slate-200" data-testid="recommendation-category-select">
                  <SelectValue placeholder="Selecciona una categoría" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Text */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <Label htmlFor="text" className="text-sm font-medium text-slate-700">
                  Tu recomendación *
                </Label>
                <span className={`text-xs ${formData.text.length > 280 ? 'text-red-500' : 'text-slate-500'}`}>
                  {formData.text.length}/280
                </span>
              </div>
              <Textarea
                id="text"
                required
                value={formData.text}
                onChange={(e) => handleChange('text', e.target.value)}
                className="mt-1 border-slate-200 min-h-[100px]"
                placeholder="Ej: Asesoría clara, documentación correcta y seguimiento puntual durante todo el proceso."
                data-testid="recommendation-text-input"
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="recommendation-error">
                {error}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-slate-200 text-slate-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.category || formData.text.length > 280}
                className="flex-1 bg-slate-900 hover:bg-slate-800"
                data-testid="recommendation-submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : (
                  <>
                    <MessageSquarePlus className="w-4 h-4 mr-2" />
                    Enviar
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
